import { DomainError } from "@/data/repository"
import { labelsFrom, type MergeOptions } from "@/lib/merge-on-conflict"
import type { StageAssigneeMap } from "@/lib/stage-assignments"
import type { EditorialFunction, EditorialTeamMembership, WorkspaceUser } from "@/types/domain"

export const editorialFunctions: EditorialFunction[] = ["LEADERSHIP", "EDITOR_IN_CHIEF", "COPY_EDITOR", "TEXT_REVIEWER", "LAYOUT_ARTIST", "DESIGNER", "PROOFREADER", "TRANSLATOR", "EDITORIAL_ASSISTANT"]

export const editorialFunctionLabels: Record<EditorialFunction, string> = {
  LEADERSHIP: "Liderança",
  EDITOR_IN_CHIEF: "Editor-chefe",
  COPY_EDITOR: "Preparador de texto",
  TEXT_REVIEWER: "Revisor de texto",
  LAYOUT_ARTIST: "Diagramador",
  DESIGNER: "Designer",
  PROOFREADER: "Revisor de prova",
  TRANSLATOR: "Tradutor",
  EDITORIAL_ASSISTANT: "Assistente editorial",
}

type TeamMember = EditorialTeamMembership & { id: string }

/** Usuários que exercem a função no selo. */
export function teamCandidates<T extends TeamMember>(users: T[], imprintId: string, editorialFunction: EditorialFunction) {
  return users.filter((user) => user.functions.includes(editorialFunction) && user.imprintIds.includes(imprintId))
}

/** Candidatos de cada etapa, pela função vinculada a ela. */
export function stageCandidateIds(users: TeamMember[], imprintId: string, stages: Array<{ id: string; editorialFunction?: EditorialFunction | null }>) {
  return Object.fromEntries(stages.flatMap((stage) => stage.editorialFunction
    ? [[stage.id, teamCandidates(users, imprintId, stage.editorialFunction).map((user) => user.id)]]
    : [])) as Record<string, string[]>
}

/**
 * Pré-preenchimento ao iniciar uma produção: o editor-chefe do selo como
 * responsável geral e, em cada etapa, o usuário com a função no selo. Funções
 * sem candidato ou com mais de um candidato ficam em branco.
 */
export function suggestProductionAssignments(users: TeamMember[], imprintId: string, stages: Array<{ id: string; editorialFunction?: EditorialFunction | null }>) {
  const single = (editorialFunction: EditorialFunction) => {
    const candidates = teamCandidates(users, imprintId, editorialFunction)
    return candidates.length === 1 ? candidates[0].id : undefined
  }
  const assignees: StageAssigneeMap = {}
  for (const stage of stages) {
    const userId = stage.editorialFunction ? single(stage.editorialFunction) : undefined
    if (userId) assignees[stage.id] = userId
  }
  return { leadUserId: single("EDITOR_IN_CHIEF") ?? "", assignees }
}

/** Mensagem de erro ao alterar a equipe, detalhando conflitos de editor-chefe. */
export function teamErrorMessage(cause: unknown) {
  if (cause instanceof DomainError && cause.code === "EDITOR_IN_CHIEF_CONFLICT") {
    const conflict = cause.details?.parsed as { imprint?: string; user?: string } | undefined
    if (conflict?.imprint && conflict.user) return `${conflict.imprint} já tem ${conflict.user} como editor-chefe. Cada selo tem apenas um editor-chefe.`
  }
  return cause instanceof Error ? cause.message : "Não foi possível concluir a operação."
}

/** Nome e equipe editorial do usuário, no formato usado na mescla quando duas pessoas editam o mesmo usuário. */
export function userTeamOf(user: WorkspaceUser) {
  return { name: user.name, functions: user.functions, imprintIds: user.imprintIds }
}

/** Funções e selos são conjuntos: o que cada pessoa acrescentou ou tirou vale, sem conflito. */
export const userTeamMergeOptions: MergeOptions = { label: labelsFrom({ name: "Nome" }), setArrays: ["functions", "imprintIds"] }

/** Funções na ordem editorial, sem repetições. */
export function sortEditorialFunctions(functions: EditorialFunction[]) {
  return editorialFunctions.filter((editorialFunction) => functions.includes(editorialFunction))
}
